import React, { useEffect } from 'react'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { SettingsBox } from 'src/Elements/Data'
import { CompanySearch } from 'src/Libs/inputs/react-hook-form'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, CircularProgress, Stack } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { CompanyConfigurationFormModel } from 'src/Libs/installations/models/installation.model'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCompanyConfigurationFormInitializer } from 'src/Libs/installations/hooks/installation-configuration/use-company-configuration-form-initializer'
import SaveConfigurationButton from './SaveConfigurationButton'
import { useAccountLinkList, useCreateAccountLink, useChangeCompany } from 'src/Libs/installation-details/hooks'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { ApiAccountRole } from 'src/Libs/api'

interface Props {
  installationId: number
  installationLoading: boolean
}

const CompanySettings: React.FC<Props> = ({ installationId, installationLoading }) => {
  const { t } = useCustomTranslation()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const [accountLinks, accountLinksLoading, refetchAccountLinks] = useAccountLinkList(installationId, 'company')
  const { initialValues, validationSchema } = useCompanyConfigurationFormInitializer(accountLinks?.[0] ?? undefined)

  const { mutateAsync: changeCompany } = useChangeCompany({
    onSuccess: () => {
      enqueueSuccessStack(t('installation-configuration.company-settings.save-success'))
      refetchAccountLinks()
    },
    onError: () => {
      enqueueErrorStack(t('installation-configuration.company-settings.save-error'))
    },
  })

  const { mutateAsync: createAccountLink } = useCreateAccountLink({
    onSuccess: () => {
      enqueueSuccessStack(t('installation-configuration.company-settings.save-success'))
      refetchAccountLinks()
    },
    onError: () => {
      enqueueErrorStack(t('installation-configuration.company-settings.save-error'))
    },
  })

  const methods = useForm<CompanyConfigurationFormModel>({
    defaultValues: initialValues,
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
  })

  useEffect(() => {
    methods.reset(initialValues)
  }, [initialValues]) //eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (data: CompanyConfigurationFormModel): Promise<void> => {
    const permissionType = 'rw'

    if (!data.companyId) return

    try {
      if (accountLinks.length > 0) {
        const currentAccountLinkId = accountLinks[0]?.id
        if (!currentAccountLinkId) return

        await changeCompany({
          installationId,
          accountLinkId: currentAccountLinkId,
          payload: {
            accountId: data.companyId.toString(),
            accountSource: 'e3dc',
            accountType: 'company',
            role: 'installer' as ApiAccountRole,
            permissionType,
            storagePermissionType: permissionType,
            reason: 'installation updated',
          },
        })
      } else {
        await createAccountLink({
          installationId,
          payload: {
            accountId: data.companyId,
            accountSource: 'e3dc',
            accountType: 'company',
            role: 'installer' as ApiAccountRole,
            permissionType,
            storagePermissionType: permissionType,
            reason: 'installation updated',
          },
        })
      }
    } catch (e) {
      console.error('could not update installation')
    }
  }

  return (
    <FormProvider {...methods}>
      <AccordionItem title={t('installation-configuration.company-settings.title')} expanded accordionDetailsSx={{ px: 4, py: 2 }}>
        <Stack justifyContent={'center'} alignItems={'center'}>
          {installationLoading || accountLinksLoading ? (
            <CircularProgress />
          ) : (
            <Box sx={{ width: '100%' }}>
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                <SettingsBox
                  title={'installation-configuration.company-settings.company'}
                  suffix={<CompanySearch name={'companyId'} initialSearchValue={accountLinks[0]?.companyName} />}
                />
                <SaveConfigurationButton />
              </form>
            </Box>
          )}
        </Stack>
      </AccordionItem>
    </FormProvider>
  )
}
export default CompanySettings
